import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Field,
  Flex,
  HStack,
  Heading,
  Icon,
  Input,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { LuBuilding2, LuLandmark, LuShieldCheck } from 'react-icons/lu';
import { signIn, useSession } from '@/lib/auth';

type LocationState = { from?: { pathname: string } } | null;

export function Login() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (session.data) {
    const from = (location.state as LocationState)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await signIn.email({ email, password });
      if (res.error) {
        setError(res.error.message ?? 'Credenciales inválidas');
        return;
      }
      const from = (location.state as LocationState)?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo iniciar sesión');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Flex minH="100dvh" bg="bg.canvas">
      <Flex
        flex="1"
        display={{ base: 'none', md: 'flex' }}
        bgGradient="to-br"
        gradientFrom="brand.700"
        gradientTo="brand.900"
        color="white"
        p="14"
        direction="column"
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          inset="0"
          opacity="0.10"
          bgImage="radial-gradient(circle at 20% 20%, white 1px, transparent 1.5px), radial-gradient(circle at 80% 60%, white 1px, transparent 1.5px)"
          bgSize="30px 30px, 50px 50px"
        />
        <HStack gap="3" position="relative">
          <Flex
            w="11"
            h="11"
            align="center"
            justify="center"
            rounded="lg"
            bg="white"
            color="brand.700"
            shadow="lg"
          >
            <Icon as={LuLandmark} boxSize="6" />
          </Flex>
          <VStack align="start" gap="0">
            <Text fontWeight="800" fontSize="lg">
              CrediFacilGT
            </Text>
          </VStack>
        </HStack>
        <Box flex="1" />
        <Stack gap="6" position="relative" maxW="md">
          <Heading size="2xl" lineHeight="1.05" letterSpacing="-0.02em">
            De la venta al cierre de cartera, en una sola plataforma.
          </Heading>
          <Text fontSize="md" opacity={0.85} lineHeight="1.55">
            Contratos con plan de pagos vivo, cobranza con buckets de mora, dashboards en tiempo
            real. Auditable de extremo a extremo.
          </Text>
          <HStack gap="3" pt="3" opacity={0.85}>
            <Icon as={LuShieldCheck} />
            <Text fontSize="sm">Acceso por invitación. Sin registro público.</Text>
          </HStack>
        </Stack>
        <Box flex="1" />
        <HStack gap="2" position="relative" fontSize="xs" opacity={0.75}>
          <Icon as={LuBuilding2} />
          {new Date().getFullYear()} · Zeezal
        </HStack>
      </Flex>

      <Flex flex="1" align="center" justify="center" p={{ base: '5', md: '10' }}>
        <Container maxW="md">
          <Stack gap="8">
            <HStack gap="3" display={{ base: 'flex', md: 'none' }}>
              <Flex
                w="11"
                h="11"
                align="center"
                justify="center"
                rounded="lg"
                bg="brand.600"
                color="white"
                shadow="sm"
              >
                <Icon as={LuLandmark} boxSize="6" />
              </Flex>
              <VStack align="start" gap="0">
                <Text fontWeight="800" fontSize="md" color="fg">
                  CrediFacilGT
                </Text>
              </VStack>
            </HStack>

            <Stack gap="2">
              <Text
                fontSize="sm"
                color="fg.muted"
                letterSpacing="0.05em"
                textTransform="uppercase"
              >
                Acceso interno
              </Text>
              <Heading size={{ base: 'lg', md: 'xl' }} letterSpacing="-0.02em">
                Iniciar sesión
              </Heading>
              <Text color="fg.muted" fontSize="sm">
                Usa el correo y contraseña que tu administrador te asignó.
              </Text>
            </Stack>

            <form onSubmit={onSubmit}>
              <Stack gap="4">
                <Field.Root>
                  <Field.Label>Correo</Field.Label>
                  <Input
                    size="lg"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    placeholder="usuario@empresa.com"
                    required
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Contraseña</Field.Label>
                  <Input
                    size="lg"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.currentTarget.value)}
                    placeholder="••••••••••••"
                    required
                  />
                </Field.Root>
                {error && (
                  <Text color="red.600" fontSize="sm">
                    {error}
                  </Text>
                )}
                <Button
                  type="submit"
                  size="lg"
                  colorPalette="brand"
                  bg="brand.600"
                  color="white"
                  _hover={{ bg: 'brand.700' }}
                  loading={submitting}
                  loadingText="Ingresando…"
                >
                  Ingresar
                </Button>
              </Stack>
            </form>

            <Box borderTopWidth="1px" borderColor="border" pt="5">
              <Text fontSize="xs" color="fg.muted" lineHeight="1.55">
                ¿Olvidaste tu contraseña? Solicita un reinicio a tu administrador. Por seguridad, no
                hay registro público ni recuperación automática.
              </Text>
            </Box>
          </Stack>
        </Container>
      </Flex>
    </Flex>
  );
}
